import { useState } from "react";
import { Sparkles, Lightbulb } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { Button } from "@component/button";
import { Textarea } from "@component/textarea";
import { Label } from "@component/label";
import ConfirmGenerateInterviewDialog from "./dialog-confirm-generate-interview";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onGenerate: (prompt: string) => void;
}

export default function GenerateInterviewDialog({
    isOpen,
    onClose,
    onGenerate,
}: Props) {
    const [prompt, setPrompt] = useState("");
    const [showWarning, setShowWarning] = useState(false);

    const handleGenerate = () => {
        if (prompt.trim()) {
            onGenerate(prompt.trim());
            setPrompt("");
            onClose();
        }
    };

    const handleClose = () => {
        setPrompt("");
        onClose();
    };

    const handleOnOpenWarning = () => {
        const hideWarning = localStorage.getItem("hideGenerateInterviewWarning");
        if (hideWarning === "true") {
            handleGenerate();
            return;
        }
        setShowWarning(true);
    }

    return (
        <>
            <Dialog open={isOpen} onOpenChange={handleClose}>
                <DialogContent className="sm:max-w-[600px]" closeButtonClassName="top-4 right-4">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-blue-600" />
                            Generate Interview Sections
                        </DialogTitle>
                        <DialogDescription>
                            Describe the interview you want to build. The AI will generate
                            sections and questions that fit within your selected duration.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div
                            className="space-y-2"
                            style={{ paddingTop: "16px", paddingBottom: "16px" }}
                        >
                            <Label htmlFor="prompt" className="text-sm font-medium">
                                Interview Requirements
                            </Label>
                            <Textarea
                                id="prompt"
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                placeholder="Example: Create a technical interview for a Senior Frontend Developer position. Focus on React, TypeScript, and system design. Include sections for technical skills, problem-solving, and cultural fit."
                                className="min-h-[150px] resize-y"
                                autoFocus
                            />
                        </div>

                        <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
                            <Lightbulb className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />

                            <p className="text-xs text-blue-900">
                                <span className="font-semibold">Tip:</span> Include the role and
                                key skills you want to assess. The more specific you are, the
                                better the results.
                            </p>
                        </div>

                        <div className="pt-2">
                            <p className="text-xs text-muted-foreground">
                                Generating new interview sections will overwrite your existing
                                sections and questions.
                                <br />
                                This action cannot be undone.
                            </p>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleOnOpenWarning}
                            disabled={!prompt.trim()}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                            <Sparkles className="w-4 h-4" />
                            Generate Sections
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ConfirmGenerateInterviewDialog
                isOpen={showWarning}
                onClose={() => setShowWarning(false)}
                onGenerate={handleGenerate}
            />
        </>
    );
}
